import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../config/firebaseConfig';
import { isArchivedAssignment } from '../../../utils/assignmentHistory';
import { buildLeaveDayMap, fetchEmployeeLeaves } from '../../../utils/employeeLeaves';

/**
 * Is the employee actively assigned to this project? Same rule as "Proje
 * Çalışanları": at least one of their rows is neither left (unassignedAt) nor
 * passive, and the project itself is not passive.
 */
export const isActivelyAssigned = (project, employeeId) => project.projectStatus !== 'Pasif'
  && Object.values(project.employees || {}).some((row) => row?.id === employeeId && !isArchivedAssignment(row) && row.status !== 'passive');

/** How a project is named in lists. */
export const projectLabel = (project) => project.titleShort || project.title || project.id;

/**
 * What the personal calendar shows: the employee's visits, their own jobs
 * (CalendarJobs), approved leaves and the projects they work on (all of them
 * for colours and names, the active ones for linking a job).
 */
const useCalendarData = (employeeId) => {
  const [visits, setVisits] = useState([]);
  const [calendarJobs, setCalendarJobs] = useState([]);
  // Onaylanmış izinler: 'yyyy-mm-dd' → o güne düşen izin kayıtları
  const [leaveDayMap, setLeaveDayMap] = useState(() => new Map());
  const [userProjects, setUserProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchVisits = useCallback(async () => {
    const visitsSnapshot = await getDocs(query(collection(db, 'Visits'), where('employeeId', '==', employeeId)));
    setVisits(visitsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, [employeeId]);

  useEffect(() => {
    if (!employeeId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        await fetchVisits();

        const jobsSnapshot = await getDocs(query(collection(db, 'CalendarJobs'), where('employeeId', '==', employeeId)));
        setCalendarJobs(jobsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));

        // Onaylanmış izinler (İzin Yönetimi kayıtları + onaylanan talepler)
        try {
          const leaves = await fetchEmployeeLeaves(employeeId);
          setLeaveDayMap(buildLeaveDayMap(leaves));
        } catch (leaveError) {
          console.error('Error fetching leaves:', leaveError);
          setLeaveDayMap(new Map());
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [employeeId, fetchVisits]);

  // Çalışanın atandığı projeler
  useEffect(() => {
    if (!employeeId) return;
    const fetchUserProjects = async () => {
      try {
        const projectsSnapshot = await getDocs(collection(db, 'Projects'));
        const allProjects = projectsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setUserProjects(allProjects.filter((project) => project.employees
          && Object.values(project.employees).some((emp) => emp.id === employeeId)));
      } catch (error) {
        console.error('Error fetching user projects:', error);
      }
    };
    fetchUserProjects();
  }, [employeeId]);

  // İşin bağlanabileceği projeler: yalnızca aktif atandıkları
  const activeProjects = useMemo(
    () => userProjects
      .filter((project) => isActivelyAssigned(project, employeeId))
      .sort((a, b) => projectLabel(a).localeCompare(projectLabel(b), 'tr')),
    [userProjects, employeeId]
  );

  return { visits, setVisits, fetchVisits, calendarJobs, setCalendarJobs, leaveDayMap, userProjects, activeProjects, loading };
};

export default useCalendarData;
